import React, { useState, useEffect, useMemo } from 'react';
import StatementCell from './components/StatementCell';
import { Table, Input, Spin, Button, Row, DatePicker, Select, Typography, InputNumber, Flex } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import { useAdminAccess } from '../../contexts/AdminAccessContext';
import type { DatePickerProps } from 'antd';
import { Dayjs } from 'dayjs';
import './Accounts.css';
import AccountDetailsModal from './components/AccountDetailsModal';
import useUserAccountsExternalBankAccounts from '../../hooks/useUserAccountsExternalBankAccounts';
import { Account } from '../../types/Account';
import AddAccountModal from './components/AddAccountModal';
import { decodeToken } from 'react-jwt';
import { mapStatusForClientSimple } from '../../utils/statusMapping';

const { Text } = Typography;

interface AccountData {
  paymentAmount: number;
  sendOnDate: Dayjs | null;
  payFromAccount: string;
}



const Accounts: React.FC = () => {
  const { loading, error, data } = useUserAccountsExternalBankAccounts({ userId: Number(localStorage.getItem('userId')) });
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [addAccountModalVisible, setAddAccountModalVisible] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [accountData, setAccountData] = useState<{ [key: string]: AccountData }>({});

  // Get admin access state from context
  const { hasAdminAccess, isTrueAdmin } = useAdminAccess();

  const token = localStorage.getItem('accessToken');
  const decoded: any = token ? decodeToken(token) : null;
  const loggedInUserId = decoded?.id;
  const storedUserId = Number(localStorage.getItem('userId'));
  const isImpersonating = hasAdminAccess && loggedInUserId && storedUserId !== Number(loggedInUserId);
  const onAddAccountClick = () => {
    setAddAccountModalVisible(true);
  };

  const onSendOnChange = (accountId: string): DatePickerProps['onChange'] => (date, dateString) => {
    setAccountData(prevData => ({
      ...prevData,
      [accountId]: {
        ...prevData[accountId],
        sendOnDate: date as Dayjs,
      }
    }));
  };

  const onPayFromChange = (accountId: string) => (value: string) => {
    setAccountData(prevData => ({
      ...prevData,
      [accountId]: {
        ...prevData[accountId],
        payFromAccount: value,
      }
    }));
  };

  useEffect(() => {
    if (data) {
      const sortedAccounts = [...data.user.accounts].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setFilteredAccounts(sortedAccounts);
    }
  }, [data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (data) {
      const filtered = data.user.accounts
        .filter(account => account.name.toLowerCase().includes(value.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFilteredAccounts(filtered);
    }
  };

  const maskAccountNumber = (accountNumber: string) => {
    return `...${accountNumber.slice(-4)}`;
  };

  const externalBankAccountOptions = data?.user?.externalBankAccounts.map((e) => {
    return {
      value: e.id,
      label: e.accountNickName + maskAccountNumber(e.accountNumber)
    };
  });

  const showModal = (accountId: number) => {
    setSelectedAccountId(accountId);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedAccountId(null);
  };
  const handleAddAccountModalClose = () => {
    setAddAccountModalVisible(false);
  };

  const handlePaymentAmountChange = (id: string, value: number | string | undefined) => {
    const amount = Number(value) || 0;
    setAccountData(prevData => ({
      ...prevData,
      [id]: {
        ...prevData[id],
        paymentAmount: amount,
      }
    }));
  };

  const adminColumns: ColumnsType<Account> = [
    {
      title: 'Account',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Account, b: Account) => a.name.localeCompare(b.name),
      render: (text, account) => (
        <>
          <span>{text}</span>
          <br />
          {maskAccountNumber(account.accountNumber)}
        </>
      ),
    },
    {
      title: 'Account Number',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      sorter: (a: Account, b: Account) => a.accountNumber.localeCompare(b.accountNumber),
      render: (accountNumber) => maskAccountNumber(accountNumber),
    },
    {
      title: 'Category',
      dataIndex: 'serviceCategoryName',
      key: 'category',
      sorter: (a: Account, b: Account) => {
        const categoryA = a.serviceCategoryName || '';
        const categoryB = b.serviceCategoryName || '';
        return categoryA.localeCompare(categoryB);
      },
      render: (text) => text || 'N/A',
    },
    {
      title: 'Last Bill Amount',
      key: 'lastBillAmount',
      render: (_, account) => (
        <StatementCell account={account} field="lastBillAmount" />
      )
    },
    {
      title: 'Next Due Date',
      key: 'dueDate',
      sorter: (a: Account, b: Account) => {
        const dueDateA = a.latestStatement?.dueDate;
        const dueDateB = b.latestStatement?.dueDate;

        const timeA = dueDateA ? new Date(dueDateA).getTime() : Number.MAX_SAFE_INTEGER;
        const timeB = dueDateB ? new Date(dueDateB).getTime() : Number.MAX_SAFE_INTEGER;

        return timeA - timeB;
      },
      render: (_, account) => (
        <StatementCell account={account} field="dueDate" />
      )
    },
    {
      title: 'Status',
      key: 'status',
      sorter: (a: Account, b: Account) => {
        const statusA = a.latestStatement?.displayStatus || '';
        const statusB = b.latestStatement?.displayStatus || '';

        const mappedStatusA = mapStatusForClientSimple(statusA);
        const mappedStatusB = mapStatusForClientSimple(statusB);

        return mappedStatusA.toLowerCase().localeCompare(mappedStatusB.toLowerCase());
      },
      render: (_, account) => (
        <StatementCell account={account} field="status" />
      )
    },
  ];

  if (!isImpersonating) {
    adminColumns.push(
      {
        title: 'Payment Amount',
        key: 'paymentAmount',
        sorter: (a: Account, b: Account) => {
          const amountA = accountData[a.id]?.paymentAmount || 0;
          const amountB = accountData[b.id]?.paymentAmount || 0;
          return amountA - amountB;
        },
        render: (_, account) => (
          <InputNumber
            prefix='$'
            onClick={(e) => e.stopPropagation()}
            style={{ width: 100 }}
            value={accountData[account.id]?.paymentAmount || ''}
            onChange={(e) => handlePaymentAmountChange(account.id, e?.valueOf())}
          />
        ),
      },
      {
        title: 'Send On',
        key: 'sendOn',
        sorter: (a: Account, b: Account) => {
          const dateA = accountData[a.id]?.sendOnDate;
          const dateB = accountData[b.id]?.sendOnDate;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.valueOf() - dateB.valueOf();
        },
        render: (_, account) => (
          <DatePicker
            value={accountData[account.id]?.sendOnDate || null}
            onChange={onSendOnChange(account.id)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        title: 'Pay From',
        key: 'payFrom',
        sorter: (a: Account, b: Account) => {
          const payFromA = accountData[a.id]?.payFromAccount || '';
          const payFromB = accountData[b.id]?.payFromAccount || '';
          return payFromA.localeCompare(payFromB);
        },
        render: (_, account) => (
          <Select
            style={{ width: 120 }}
            value={accountData[account.id]?.payFromAccount || undefined}
            onChange={onPayFromChange(account.id)}
            onClick={(e) => e.stopPropagation()}
            options={externalBankAccountOptions}
          />
        ),
      }
    );
  }



  if (loading) return <Spin />;
  if (error) return <p>Error loading accounts.</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <h1>Accounts</h1>
      <Row justify="space-between">
        <span>
          <Input
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearch}
            style={{ marginBottom: 20, width: 200, marginRight: 10 }}
            prefix={<SearchOutlined />}
          />
          {hasAdminAccess && (
          <Button type="primary" style={{ marginBottom: 0 }} onClick={onAddAccountClick}>
            + Add Account
          </Button>)}
        </span>
      </Row>
      <div className="accounts-container">
        {hasAdminAccess ? (
          // Admin view with payment controls and all client columns
          <Table
            dataSource={filteredAccounts}
            rowKey="id"
            onRow={(record) => ({ onClick: () => showModal(Number(record.id)) })}
            rowClassName="clickable-row"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredAccounts.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} accounts`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                  setCurrentPage(1);
                }
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setCurrentPage(1);
              }
            }}
            columns={adminColumns}
          />
        ) : (
          // Client view with statement info
          <Table
            dataSource={filteredAccounts}
            rowKey="id"
            onRow={(record) => ({ onClick: () => showModal(Number(record.id)) })}
            rowClassName="clickable-row"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredAccounts.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} accounts`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                  setCurrentPage(1);
                }
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setCurrentPage(1);
              }
            }}
            columns={[
              {
                title: 'Account',
                dataIndex: 'name',
                key: 'name',
                sorter: (a: Account, b: Account) => a.name.localeCompare(b.name),
                render: (text, account) => (
                  <>
                    <span>{text}</span>
                    <br />
                    {maskAccountNumber(account.accountNumber)}
                  </>
                ),
              },
              {
                title: 'Category',
                dataIndex: 'serviceCategoryName',
                key: 'category',
                sorter: (a: Account, b: Account) => {
                  const categoryA = a.serviceCategoryName || '';
                  const categoryB = b.serviceCategoryName || '';
                  return categoryA.localeCompare(categoryB);
                },
                render: (text) => text || 'N/A',
              },
              {
                title: 'Last Bill Amount',
                key: 'lastBillAmount',
                render: (_, account) => (
                  <StatementCell account={account} field="lastBillAmount" />
                )
              },
              {
                title: 'Next Due Date',
                key: 'dueDate',
                sorter: (a: Account, b: Account) => {
                  const dueDateA = a.latestStatement?.dueDate;
                  const dueDateB = b.latestStatement?.dueDate;

                  const timeA = dueDateA ? new Date(dueDateA).getTime() : Number.MAX_SAFE_INTEGER;
                  const timeB = dueDateB ? new Date(dueDateB).getTime() : Number.MAX_SAFE_INTEGER;

                  return timeA - timeB;
                },
                render: (_, account) => (
                  <StatementCell account={account} field="dueDate" />
                )
              },
              {
                title: 'Status',
                key: 'status',
                sorter: (a: Account, b: Account) => {
                  const statusA = a.latestStatement?.displayStatus || '';
                  const statusB = b.latestStatement?.displayStatus || '';

                  // Always map statuses for client interface before comparing
                  const mappedStatusA = mapStatusForClientSimple(statusA);
                  const mappedStatusB = mapStatusForClientSimple(statusB);

                  return mappedStatusA.toLowerCase().localeCompare(mappedStatusB.toLowerCase());
                },
                render: (_, account) => (
                  <StatementCell account={account} field="status" />
                )
              },
            ]}
          />
        )}
      </div>
      <AccountDetailsModal
        accountId={selectedAccountId}
        visible={modalVisible}
        onClose={handleModalClose}
      />
      <AddAccountModal
        visible={addAccountModalVisible}
        onClose={handleAddAccountModalClose}
      />
    </div >
  );
};

export default Accounts;
